import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { PRD3030_R01Dto, PRD3030_R01GridRowData, PRD3030_R01SubtotalData, PRD3030_R01DateSubtotalData, PRD3030_R01ProductResultLotDto } from './Dto_PRD3030_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface PRD3030_R01Filter {
    compCd: string;
    productDateRange: DateRange | undefined;
    orderType: string;
    workplaceCd: string;
    itemCd: string;
    itemNm: string;
    brandCd: string;
}

// PRD3040_R01Dto에 isSubtotal 속성 추가 (기본값: false)
export interface ExtendedPRD3030_R01Dto extends PRD3030_R01Dto {
    isSubtotal: false;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_PRD3030_R01 {
    PRD3030_R01Filter: PRD3030_R01Filter;
    PRD3030_R01PastFilter: PRD3030_R01Filter;
    setPRD3030_R01Filter: (filter: Partial<PRD3030_R01Filter>) => void;
    setPRD3030_R01PastFilter: (filter: Partial<PRD3030_R01Filter>) => void;

    // === 리스트 관련 ===
    PRD3030_R01List: ExtendedPRD3030_R01Dto[];
    setPRD3030_R01List: (PRD3030_R01List: ExtendedPRD3030_R01Dto[]) => void;
    insertPRD3030_R01List: (data: ExtendedPRD3030_R01Dto[], index?: number) => void;
    removePRD3030_R01List: (index: number) => void;
    updatePRD3030_R01List: (data: ExtendedPRD3030_R01Dto, index: number) => void;

    // === 선택 관련 ===
    selectedPRD3030_R01List: string[];
    setSelectedPRD3030_R01List: (idList: string[]) => void;
    addSelectedPRD3030_R01List: (idList: string[]) => void;
    removeSelectedPRD3030_R01List: (idList?: string[]) => void;
    initSelectedPRD3030_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    // === 통합 소계 관련 ===
    showSubtotals: boolean;              // 통합 소계 표시 여부 (품목별 + 생산일자별)
    setShowSubtotals: (show: boolean) => void;
    subtotalData: PRD3030_R01SubtotalData[];        // 품목별 소계 데이터
    setSubtotalData: (data: PRD3030_R01SubtotalData[]) => void;
    dateSubtotalData: PRD3030_R01DateSubtotalData[]; // 생산일자별 소계 데이터
    setDateSubtotalData: (data: PRD3030_R01DateSubtotalData[]) => void;
    gridRowData: PRD3030_R01GridRowData[];
    setGridRowData: (data: PRD3030_R01GridRowData[]) => void;

    // === LOT 조회 관련 ===
    lotList: PRD3030_R01ProductResultLotDto[];          // LOT 리스트
    setLotList: (list: PRD3030_R01ProductResultLotDto[]) => void;
    selectedProductResultNo: string;         // 선택된 생산실적번호
    setSelectedProductResultNo: (no: string) => void;
    selectedLot: PRD3030_R01ProductResultLotDto | undefined;  // 선택된 LOT
    setSelectedLot: (lot: PRD3030_R01ProductResultLotDto | undefined) => void;
    lotColumnState: ColumnState[] | null;    // LOT 그리드 컬럼 상태
    setLotColumnState: (state: ColumnState[] | null) => void;
    lotScrollTop: number | null;
    setLotScrollTop: (top: number | null) => void;

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultPRD3030_R01Filter: PRD3030_R01Filter = {
    compCd: '',
    productDateRange: defaultDateRange,
    orderType: '',
    workplaceCd: '',
    itemCd: '',
    itemNm: '',
    brandCd: '',
};

/**
 * Zustand 스토어 정의
 */
export const usePRD3030_R01Store = create<Store_PRD3030_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_PRD3030_R01, 'PRD3030_R01Filter' | 'PRD3030_R01PastFilter'>) => 
        (filter: Partial<PRD3030_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /**
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        PRD3030_R01Filter: defaultPRD3030_R01Filter,
        PRD3030_R01PastFilter: defaultPRD3030_R01Filter,
        setPRD3030_R01Filter: createFilterSetter('PRD3030_R01Filter'),
        setPRD3030_R01PastFilter: createFilterSetter('PRD3030_R01PastFilter'),

        // === 리스트 관련 ===
        PRD3030_R01List: [],
        setPRD3030_R01List: (PRD3030_R01List: ExtendedPRD3030_R01Dto[]) => set({ PRD3030_R01List }),
        insertPRD3030_R01List: (data: ExtendedPRD3030_R01Dto[], index?: number) => set((state) => {
            const _PRD3030_R01List = [...state.PRD3030_R01List];
            
            if (index !== undefined) {
                _PRD3030_R01List.splice(index, 0, ...data);
            } else {
                _PRD3030_R01List.push(...data);
            }
            return { ...state, PRD3030_R01List: _PRD3030_R01List };
        }),
        removePRD3030_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            PRD3030_R01List: state.PRD3030_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updatePRD3030_R01List: (data: ExtendedPRD3030_R01Dto, index: number) => set((state) => {
            const _PRD3030_R01List = [...state.PRD3030_R01List];
            _PRD3030_R01List[index] = data;
            return { ...state, PRD3030_R01List: _PRD3030_R01List };
        }),

        // === 선택 관련 ===
        selectedPRD3030_R01List: [],
        setSelectedPRD3030_R01List: (idList: string[]) => set({ selectedPRD3030_R01List: idList, lotScrollTop: null }),
        addSelectedPRD3030_R01List: (idList: string[]) => set((state) => {
            const _selectedPRD3030_R01List = [...state.selectedPRD3030_R01List];
            _selectedPRD3030_R01List.push(...idList.filter(value => !_selectedPRD3030_R01List.includes(value)));
            return { ...state, selectedPRD3030_R01List: _selectedPRD3030_R01List };
        }),
        removeSelectedPRD3030_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedPRD3030_R01List: [] };
            }
            
            return {
                ...state,
                selectedPRD3030_R01List: removeItemsFromArray(state.selectedPRD3030_R01List, idList),
            };
        }),
        initSelectedPRD3030_R01List: () => set({ selectedPRD3030_R01List: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        // === 통합 소계 관련 ===
        showSubtotals: false,
        setShowSubtotals: (show: boolean) => set({ showSubtotals: show }),
        subtotalData: [],
        setSubtotalData: (data: PRD3030_R01SubtotalData[]) => set({ subtotalData: data }),
        dateSubtotalData: [],
        setDateSubtotalData: (data: PRD3030_R01DateSubtotalData[]) => set({ dateSubtotalData: data }),
        gridRowData: [],
        setGridRowData: (data: PRD3030_R01GridRowData[]) => set({ gridRowData: data }),

        // === LOT 조회 관련 ===
        lotList: [],
        setLotList: (list: PRD3030_R01ProductResultLotDto[]) => set({ lotList: list }),
        selectedProductResultNo: '',
        setSelectedProductResultNo: (no: string) => set({ selectedProductResultNo: no, lotScrollTop: null }),
        selectedLot: undefined,
        setSelectedLot: (lot: PRD3030_R01ProductResultLotDto | undefined) => set({ selectedLot: lot }),
        lotColumnState: null,
        setLotColumnState: (state: ColumnState[] | null) => set({ lotColumnState: state }),
        lotScrollTop: null,
        setLotScrollTop: (top: number | null) => set({ lotScrollTop: top }),

        // === 전체 초기화 ===
        initData: () => set(() => ({
            PRD3030_R01Filter: defaultPRD3030_R01Filter,
            PRD3030_R01PastFilter: defaultPRD3030_R01Filter,
            PRD3030_R01List: [],
            selectedPRD3030_R01List: [],
            columnState: null,
            scrollTop: null,
            allCount: 0,
		
            showSubtotals: false,
            subtotalData: [],
            dateSubtotalData: [],
            gridRowData: [],
            lotList: [],
            selectedProductResultNo: '',
            selectedLot: undefined,
            lotColumnState: null,
            lotScrollTop: null,
        })),
    };
});
